import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGolfApi, GolfCourse, GolfHole } from '../services/golfApi';
import { searchGolfCourses, getCourseDetails, ExternalCourse } from '../services/externalGolfApi';
import { MapPin, Plus, Edit2, Trash2, Save, X, Search, Download, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const GolfCoursesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const golfApi = useGolfApi();
  const [isCreating, setIsCreating] = useState(false);
  const [editingCourse, setEditingCourse] = useState<GolfCourse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['golf', 'courses'],
    queryFn: () => golfApi.getCourses(),
  });

  const createMutation = useMutation({
    mutationFn: (course: Partial<GolfCourse>) => golfApi.createCourse(course),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'courses'] });
      setIsCreating(false);
      toast.success('Course created successfully');
    },
    onError: () => {
      toast.error('Failed to create course');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ courseId, updates }: { courseId: string; updates: Partial<GolfCourse> }) =>
      golfApi.updateCourse(courseId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'courses'] });
      setEditingCourse(null);
      toast.success('Course updated successfully');
    },
    onError: () => {
      toast.error('Failed to update course');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (courseId: string) => golfApi.deleteCourse(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf', 'courses'] });
      toast.success('Course deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete course');
    },
  });

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <MapPin className="h-10 w-10 text-green-600" />
              Golf Courses
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage golf courses and hole information
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Import Course
            </button>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Manually
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search courses..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* Create Course Modal */}
      {isCreating && (
        <CourseFormModal
          onClose={() => setIsCreating(false)}
          onSubmit={(course) => createMutation.mutate(course)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {/* Edit Course Modal */}
      {editingCourse && (
        <CourseFormModal
          course={editingCourse}
          onClose={() => setEditingCourse(null)}
          onSubmit={(updates) =>
            updateMutation.mutate({ courseId: editingCourse.courseId, updates })
          }
          isSubmitting={updateMutation.isPending}
        />
      )}

      {/* Import Course Modal */}
      {showImportModal && (
        <ImportCourseModal
          onClose={() => setShowImportModal(false)}
          onImport={(course) => {
            createMutation.mutate(course);
            setShowImportModal(false);
          }}
        />
      )}

      {/* Courses List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCourses.map((course) => (
          <div
            key={course.courseId}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {/* Course Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-2xl font-bold">{course.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCourse(course)}
                    className="p-2 hover:bg-white/20 rounded transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this course?')) {
                        deleteMutation.mutate(course.courseId);
                      }
                    }}
                    className="p-2 hover:bg-white/20 rounded transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-green-100">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{course.location}</span>
              </div>
            </div>

            {/* Course Details */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {course.holes?.length || 18}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Holes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {course.totalPar}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Par</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {course.totalYardage?.toLocaleString() || '-'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Yards</div>
                </div>
              </div>

              {course.address && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  📍 {course.address}
                </p>
              )}
              {course.phone && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  📞 {course.phone}
                </p>
              )}
              {course.website && (
                <a
                  href={course.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-600 hover:text-green-700 dark:text-green-400"
                >
                  🌐 Visit Website →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No courses found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Add your first golf course to get started
          </p>
        </div>
      )}
    </div>
  );
};

// Course Form Modal Component
interface CourseFormModalProps {
  course?: GolfCourse;
  onClose: () => void;
  onSubmit: (course: Partial<GolfCourse>) => void;
  isSubmitting: boolean;
}

const CourseFormModal: React.FC<CourseFormModalProps> = ({ course, onClose, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: course?.name || '',
    location: course?.location || '',
    address: course?.address || '',
    phone: course?.phone || '',
    website: course?.website || '',
    holes: course?.holes || Array.from({ length: 18 }, (_, i) => ({
      holeNumber: i + 1,
      par: 4,
      yardage: 350,
      handicap: i + 1,
    })),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateHole = (index: number, field: keyof GolfHole, value: number) => {
    const newHoles = [...formData.holes];
    newHoles[index] = { ...newHoles[index], [field]: value };
    setFormData({ ...formData, holes: newHoles });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {course ? 'Edit Course' : 'Add New Course'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Course Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Location *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                placeholder="City, State"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Holes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Hole Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              {formData.holes.map((hole, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="font-semibold text-gray-900 dark:text-white mb-3">
                    Hole {hole.holeNumber}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Par</label>
                      <input
                        type="number"
                        min="3"
                        max="5"
                        value={hole.par}
                        onChange={(e) => updateHole(index, 'par', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Yards</label>
                      <input
                        type="number"
                        min="100"
                        max="700"
                        value={hole.yardage}
                        onChange={(e) => updateHole(index, 'yardage', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">HCP</label>
                      <input
                        type="number"
                        min="1"
                        max="18"
                        value={hole.handicap}
                        onChange={(e) => updateHole(index, 'handicap', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? 'Saving...' : course ? 'Update Course' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Import Course Modal Component
interface ImportCourseModalProps {
  onClose: () => void;
  onImport: (course: Partial<GolfCourse>) => void;
}

const ImportCourseModal: React.FC<ImportCourseModalProps> = ({ onClose, onImport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ExternalCourse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<ExternalCourse | null>(null);
  const [isLoadingHoles, setIsLoadingHoles] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a course name or city');
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchGolfCourses(searchTerm);
      setSearchResults(results);
      if (results.length === 0) {
        toast.info('No courses found. Try a different search term.');
      }
    } catch (error) {
      toast.error('Failed to search courses');
    } finally {
      setIsSearching(false);
    }
  };

  const handleImport = async (externalCourse: ExternalCourse) => {
    setSelectedCourse(externalCourse);
    setIsLoadingHoles(true);

    try {
      // Fetch hole-by-hole data
      const holes = await getCourseDetails(externalCourse.id);

      // Create course object
      const course: Partial<GolfCourse> = {
        name: externalCourse.name,
        location: `${externalCourse.city}, ${externalCourse.state}`,
        address: externalCourse.address,
        phone: externalCourse.phone,
        website: externalCourse.website,
        holes: holes,
      };

      toast.success(`Importing ${externalCourse.name}...`);
      onImport(course);
    } catch (error) {
      toast.error('Failed to import course');
      setIsLoadingHoles(false);
      setSelectedCourse(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Download className="h-6 w-6" />
              Import Golf Course
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Search and import course data automatically
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Search Form */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Course Name or City
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g., Valhalla, Louisville, Keeneland"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                💡 Tip: Search by course name (e.g., "Valhalla") or city (e.g., "Louisville")
              </p>
            </div>

            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
            >
              <Search className="h-5 w-5" />
              {isSearching ? 'Searching...' : 'Search Courses'}
            </button>
          </div>

          {/* Info Banner */}
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-300">
              ✅ <strong>Connected to Golf Course API:</strong> Search from 25,000+ courses nationwide with detailed hole-by-hole data! 
              Powered by <a href="https://api.golfcourseapi.com" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Golf Course API</a>
            </p>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Search Results ({searchResults.length})
              </h3>
              <div className="space-y-3">
                {searchResults.map((course) => (
                  <div
                    key={course.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {course.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          📍 {course.city}, {course.state}
                        </p>
                        {course.address && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {course.address}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {course.holes && <span>🏌️ {course.holes} holes</span>}
                          {course.par && <span>Par {course.par}</span>}
                          {course.yardage && <span>{course.yardage?.toLocaleString()} yards</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleImport(course)}
                        disabled={isLoadingHoles && selectedCourse?.id === course.id}
                        className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                      >
                        {isLoadingHoles && selectedCourse?.id === course.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Importing...
                          </>
                        ) : (
                          <>
                            Import
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchResults.length === 0 && !isSearching && searchTerm && (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">
                No courses found. Try a different search term.
              </p>
            </div>
          )}

          {/* Initial State */}
          {searchResults.length === 0 && !searchTerm && (
            <div className="text-center py-12">
              <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Search for a Golf Course
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Enter a course name or city to find courses to import
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Popular searches: <button onClick={() => {setSearchTerm('Louisville'); setTimeout(handleSearch, 100);}} className="text-blue-600 hover:underline">Louisville</button>, 
                <button onClick={() => {setSearchTerm('Lexington'); setTimeout(handleSearch, 100);}} className="text-blue-600 hover:underline ml-1">Lexington</button>, 
                <button onClick={() => {setSearchTerm('Valhalla'); setTimeout(handleSearch, 100);}} className="text-blue-600 hover:underline ml-1">Valhalla</button>
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-white dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GolfCoursesPage;

